import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { generateUUID } from '../lib/utils/uuid';

interface PlayerLockContextType {
  lockedPlayers: Set<string>;
  lockError: string | null;
  lockPlayers: (playerIds: string[], tournamentId: string) => Promise<boolean>;
  unlockPlayers: (playerIds: string[], tournamentId: string) => Promise<void>;
  isPlayerLocked: (playerId: string) => boolean;
}

const PlayerLockContext = createContext<PlayerLockContextType | undefined>(undefined);

export function usePlayerLock() {
  const context = useContext(PlayerLockContext);
  if (!context) {
    throw new Error('usePlayerLock must be used within PlayerLockProvider');
  }
  return context;
}

interface PlayerLockProviderProps {
  children: React.ReactNode;
}

export function PlayerLockProvider({ children }: PlayerLockProviderProps) {
  const LOCKING_ENABLED = false; // Disable player locking system
  const [lockedPlayers, setLockedPlayers] = useState<Set<string>>(new Set());
  const [lockError, setLockError] = useState<string | null>(null);
  const clientId = useRef(generateUUID());
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [isPlayerSelectionMode, setIsPlayerSelectionMode] = useState(false);
  const subscriptionRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const currentTournamentRef = useRef<string | null>(null);
  
  // Fetch locked players periodically
  useEffect(() => {
    const setupSubscription = async () => {
      try {
        // Clean up existing subscription
        if (subscriptionRef.current) {
          subscriptionRef.current.unsubscribe();
          subscriptionRef.current = null;
        }

        // Don't set up new subscription if no tournament or not in scoring mode
        if (!currentTournamentRef.current || !isPlayerSelectionMode) {
          return;
        }

        // Set up real-time subscription first
        subscriptionRef.current = supabase
          .channel('player-locks')
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'players',
              filter: `and(locked_by.neq.${clientId.current},tournament_id.eq.${currentTournamentRef.current})`
            },
            async (payload) => {
              console.log('Player lock change:', payload);
              
              // Update locked players based on the change
              if (payload.eventType === 'UPDATE') {
                const { new: newData } = payload;
                if (newData.locked_by && newData.locked_by !== clientId.current) {
                  setLockedPlayers(prev => new Set([...prev, newData.id]));
                } else {
                  setLockedPlayers(prev => {
                    const next = new Set(prev);
                    next.delete(newData.id);
                    return next;
                  });
                }
              }
            }
          )
          .subscribe((status) => {
            console.log('Player locks subscription status:', status);
          });

        // Initial fetch of locked players
        const { data, error } = await supabase
          .from('players')
          .select('id, locked_by, locked_at')
          .not('locked_by', 'is', null)
          .not('locked_by', 'eq', clientId.current)
          .eq('tournament_id', currentTournamentRef.current)
          .gte('locked_at', new Date(Date.now() - 5 * 60 * 1000).toISOString());

        if (error) throw error;
        setLockedPlayers(new Set(data?.map(p => p.id) || []));
      } catch (err) {
        console.error('Failed to fetch locked players:', err);
      }
    };

    setupSubscription();
    
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
    };
  }, [currentTournamentRef.current, isPlayerSelectionMode]);

  // Lock players
  const lockPlayers = async (playerIds: string[], tournamentId: string) => {
    if (!LOCKING_ENABLED) return true;
    try {
      setLockError(null);
      
      // First validate tournament ID
      if (!tournamentId || tournamentId === 'null') {
        setLockError('Invalid tournament ID');
        return false;
      }
      
      // Then check if tournament is active
      const { data: tournament, error: tournamentError } = await supabase
        .from('tournaments')
        .select('status')
        .eq('id', tournamentId)
        .single();

      if (tournamentError) throw tournamentError;
      
      if (!tournament || tournament.status !== 'active') {
        setLockError('Tournament must be active to start scoring');
        return false;
      }
      
      currentTournamentRef.current = tournamentId;
      
      // Enter player selection mode - use 3s refresh
      setIsPlayerSelectionMode(true);
      console.log('Attempting to lock players:', playerIds);

      // First check if any players are already locked
      const { data: lockedData } = await supabase
        .from('players')
        .select('id, locked_by, locked_at')
        .in('id', playerIds)
        .not('locked_by', 'is', null)
        .eq('tournament_id', tournamentId)
        .gte('locked_at', new Date(Date.now() - 5 * 60 * 1000).toISOString());

      if (lockedData && lockedData.length > 0) {
        console.log('Players already locked:', lockedData);
        setLockError('Players are currently being scored by another user');
        return false;
      }

      // Then attempt to lock players in a single transaction
      const { data: updateData, error: updateError } = await supabase
        .from('players')
        .update({ 
          locked_by: clientId.current,
          locked_at: new Date().toISOString()
        })
        .in('id', playerIds)
        .is('locked_by', null)
        .eq('tournament_id', tournamentId)
        .select();

      if (updateError) {
        console.error('Error locking players:', updateError);
        setLockError('Players are currently being scored by another user');
        return false;
      }

      // Check if we got locks for all requested players
      console.log('Lock update result:', updateData);
      if (!updateData || updateData.length !== playerIds.length) {
        setLockError('Some players are currently being scored by another user');
        return false;
      }

      return true;
    } catch (err) {
      setLockError('Failed to lock players');
      console.error('Failed to lock players:', err);
      return false;
    }
  };

  // Unlock players
  const unlockPlayers = async (playerIds: string[], tournamentId: string) => {
    if (!LOCKING_ENABLED) return;
    try {
      // Skip if no valid tournament ID
      if (!tournamentId || tournamentId === 'null') {
        return;
      }
      
      // Exit player selection mode - use 5min refresh
      setIsPlayerSelectionMode(false);
      console.log('Attempting to unlock players:', playerIds);
      if (!playerIds?.length) return;
      
      const { data: unlockData, error: unlockError } = await supabase
        .from('players')
        .update({ 
          locked_by: null,
          locked_at: null
        })
        .in('id', playerIds)
        .eq('locked_by', clientId.current)
        .eq('tournament_id', tournamentId)
        .select();

      if (unlockError) {
        console.error('Error unlocking players:', unlockError);
        throw unlockError;
      }

      // Remove from local state
      setLockedPlayers(prev => {
        const next = new Set(prev);
        playerIds.forEach(id => next.delete(id));
        return next;
      });
      currentTournamentRef.current = null;
    } catch (err) {
      console.error('Failed to unlock players:', err);
    }
  };

  // Check if a player is locked
  const isPlayerLocked = (playerId: string) => {
    if (!LOCKING_ENABLED) return false;
    // Only check locks if we have an active tournament and are in scoring mode
    if (!currentTournamentRef.current || !isPlayerSelectionMode) {
      return false;
    }
    return lockedPlayers.has(playerId);
  };

  // Set up refresh interval based on mode
  useEffect(() => {
    // Clear any existing interval
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }

    // Only set up refresh if we have an active tournament
    if (!currentTournamentRef.current) {
      return;
    }

    // Set new interval based on mode
    const interval = isPlayerSelectionMode ? 3000 : 300000; // 3s or 5min
    
    refreshIntervalRef.current = setInterval(async () => {
      try {
        const { data } = await supabase
          .from('players')
          .select('id, locked_by, locked_at')
          .not('locked_by', 'is', null)
          .not('locked_by', 'eq', clientId.current)
          .eq('tournament_id', currentTournamentRef.current)
          .gte('locked_at', new Date(Date.now() - 5 * 60 * 1000).toISOString());

        setLockedPlayers(new Set(data?.map(p => p.id) || []));
      } catch (err) {
        console.error('Failed to refresh locked players:', err);
      }
    }, interval);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [isPlayerSelectionMode, currentTournamentRef.current]);

  // Cleanup locks on unmount
  useEffect(() => {
    return () => {
      // Clear refresh interval
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }

      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
      
      // Only release locks if we have an active tournament
      if (currentTournamentRef.current) {
        // Release any locks held by this client
        supabase
          .from('players')
          .update({ locked_by: null, locked_at: null })
          .eq('locked_by', clientId.current)
          .eq('tournament_id', currentTournamentRef.current)
          .then(() => {
            console.log('Released player locks on unmount');
          })
          .catch(error => {
            console.error('Failed to release player locks:', error);
          });
      }
    };
  }, []);

  return (
    <PlayerLockContext.Provider value={{
      lockedPlayers,
      lockError,
      lockPlayers,
      unlockPlayers,
      isPlayerLocked
    }}>
      {children}
    </PlayerLockContext.Provider>
  );
}